import { memo, useState, useRef, forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '../../lib/styles';

export type CollapsibleProps = {
  className?: string;
  alwaysExpanded?: boolean;
  collapsedClassName?: string;
  expandedClassName?: string;
  /** A focusable element, e.g. input, button */
  children?: React.ReactElement | ((props: { isExpanded: boolean }) => React.ReactElement);
};

/**
 * A wrapper component that expands/collapses when its child is focused/blurred.
 */
const CollapsibleBase = (
  {
    alwaysExpanded = false,
    className,
    collapsedClassName = 'w-34',
    expandedClassName = 'w-64',
    children,
  }: CollapsibleProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const [isFocused, setIsFocused] = useState(false);
  const childRef = useRef<HTMLElement | null>(null);

  const handleChildFocus = () => {
    setIsFocused(true);
  };

  const handleChildBlur = () => {
    setIsFocused(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      childRef.current?.blur();
    }
  };

  const isExpanded = isFocused || alwaysExpanded;

  return (
    <div
      ref={ref}
      className={cn('relative transition-all', isExpanded ? expandedClassName : collapsedClassName, className)}
    >
      <Slot
        ref={childRef}
        onFocus={handleChildFocus}
        onBlur={handleChildBlur}
        onKeyDown={handleKeyDown}
        className={cn(
          isExpanded
            ? 'placeholder:text-text-tertiary'
            : 'placeholder:text-text-primary hover:bg-bg-inter-secondary-hover hover:border-bg-inter-secondary-hover',
        )}
      >
        {typeof children === 'function' ? children({ isExpanded }) : children}
      </Slot>
    </div>
  );
};

export const Collapsible = memo(forwardRef(CollapsibleBase));
